import React, { useEffect, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { ROOMS } from '../rooms';
import { onStack } from './hudStack';

// SceneStaging — the beat that runs between sitting down and being served.
//
// Sitting in a salon is a request. Somebody should come. This is the smallest
// honest version of that: the room says who and where (ROOMS.<id>.stage) and on
// what cue (ROOMS.<id>.onSit), and this turns the cue into a stage direction.
//
// ── WHY THE DELAY ────────────────────────────────────────────────────────────
// She could start walking on the same frame you land in the chair, and it would
// read as a trigger firing. A few seconds of nothing is what makes it read as
// her noticing — the pause is the performance, not latency to be optimised away.
//
// ── WHY THE BUTTON IS DOM ────────────────────────────────────────────────────
// Every other control in the room is in-scene, which is right for things that
// belong to an object: the chair's own "Stand up" occludes, scales with distance
// and sits in the room's light. This one is different in kind. It exists so you
// can never be stuck in a set piece, and an in-scene button can be looked away
// from — the exact moment you would want it is the moment it is behind you.
//
// It is not a second Stand Up. Standing up is something the player does to the
// chair; this ends the whole beat and sends everyone home, which is why it also
// clears the staging rather than leaving her parked at the basin.
//
// The conversation, if one is open, is separate and closes with its own × on the
// SceneDock. Ending a set piece is not a reason to hang up on somebody.
// ── IT CANNOT LIVE IN THE BOTTOM-RIGHT WHILE A CONVERSATION IS OPEN ──────────
// Khari, 2026-08-06: "There is a 'done' button that allows me to exit at any
// time.. We'd just need to make sure it is visible."
//
// It was not. SceneDock is `bottom`-anchored, centred, and up to
// `100vw - 32px` wide — so on any phone it reaches the right edge, and this
// button (z 73, one above the dock's 72) landed ON its input row. Visible in the
// sense that it painted; unusable, and it stole taps meant for Send.
//
// So while a scene is up it moves to the TOP-right, which the dock never
// occupies. Below CallRing's corner by a full row, because an incoming call and
// a seated scene can coexist and the call has to win that spot (z 90 to this
// button's 73 — it would paint over the top of this one anyway).
const btnBase = {
  position: 'fixed', right: 'max(16px, env(safe-area-inset-right))', zIndex: 73,
  padding: '11px 20px', borderRadius: 999,
  border: '1px solid rgba(255,178,94,0.5)', background: 'rgba(20,12,8,0.9)',
  color: '#ffe9c4', font: '700 14px system-ui', cursor: 'pointer',
  pointerEvents: 'auto', backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
};
// Off a scene it stands on the right-hand column (hudStack): the player bar,
// then whatever dock is open — it was pinned at 16px, on the radio and on the
// barista's Send (2026-10-31).
const btnLow = { ...btnBase, bottom: onStack(16) };
const btnHigh = { ...btnBase, top: 'calc(max(16px, env(safe-area-inset-top)) + 64px)' };

// ── TWO MINUTES IN A SHARED CHAIR (2026-10-30) ──────────────────────────────
// Khari: "This indoor room is a shared live experience (like the plaza), so
// seats will actually be limited. Prevent sitting at one if it is taken, and
// limit the time a user can sit (2 min)." A seat in a `sharedSeats` room carries
// `until` (RoomMarkers, from the server's hold or the local two minutes). The
// time left sits quietly inside Done — the one seated control that is always on
// screen — and when it runs out you are stood up with a word about why, rather
// than the chair just letting go of you. Standing is what frees the seat
// (worldSocket.releaseOnStand), so this is the same route as pressing Done.
const TIME_UP = "Your two minutes are up — the seat's free for the next person";
// A chair kept while you are busy in it (rooms.js `seatKeepAlive`, a Hype-U
// classroom: city/hypeu keepSeat) runs out only after two quiet minutes, so it
// says that — and its clock shows only in the last minute, as the warning; a
// clock jumping back to 2:00 every time the class says "still here" is noise.
const QUIET_UP = "Two quiet minutes — the seat's free for the next student";
const QUIET_SHOW_MS = 60 * 1000;
const NOTE_MS = 4500;
const note = {
  position: 'fixed', left: '50%', transform: 'translateX(-50%)',
  // Above the Done button, wherever the column has put it.
  bottom: onStack(16, 54), zIndex: 73,
  maxWidth: 'calc(100vw - 32px)', padding: '9px 16px', borderRadius: 999,
  background: 'rgba(20,12,8,0.9)', border: '1px solid rgba(255,178,94,0.35)',
  color: '#ffe9c4', font: '600 13px/1.4 system-ui', textAlign: 'center',
  pointerEvents: 'none', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
};
const left = { marginLeft: 8, fontWeight: 500, opacity: 0.6, fontVariantNumeric: 'tabular-nums' };
const clock = (ms) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export default function SceneStaging() {
  const interior = useWorldStore((s) => s.interior);
  const seated = useWorldStore((s) => s.seated);
  const scene = useWorldStore((s) => s.scene);
  const stageTo = useWorldStore((s) => s.stageTo);
  const clearStaging = useWorldStore((s) => s.clearStaging);
  const standUp = useWorldStore((s) => s.standUp);
  const standCleared = useWorldStore((s) => s.standCleared);
  const seatNote = useWorldStore((s) => s.seatNote);
  const setSeatNote = useWorldStore((s) => s.setSeatNote);
  const [, tick] = useState(0);

  const cue = ROOMS[interior]?.onSit || null;
  const keepAlive = !!ROOMS[interior]?.seatKeepAlive;
  const phase = seated?.phase || null;
  const until = seated?.until || null;

  // THE TWO MINUTES RUN OUT: stood up, and told why. Keyed on `until`, so a
  // reclaimed seat (worldSocket.reclaimSeat) keeps the clock it started with.
  useEffect(() => {
    if (!until || !phase || phase === 'standing') return undefined;
    const t = setTimeout(() => {
      standUp();
      clearStaging();
      setSeatNote(keepAlive ? QUIET_UP : TIME_UP);
    }, Math.max(0, until - Date.now()));
    return () => clearTimeout(t);
  }, [until, phase, standUp, clearStaging, setSeatNote, keepAlive]);

  // A quiet second hand for the time left — only while there is a limit.
  useEffect(() => {
    if (!until || !phase || phase === 'standing') return undefined;
    const iv = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(iv);
  }, [until, phase]);

  // A note is a reaction to a moment, so it goes by itself (keyed on `at`, so
  // the same note twice restarts its clock).
  useEffect(() => {
    if (!seatNote) return undefined;
    const t = setTimeout(() => setSeatNote(null), NOTE_MS);
    return () => clearTimeout(t);
  }, [seatNote?.at, setSeatNote]); // eslint-disable-line react-hooks/exhaustive-deps

  // LEAVING THE ROOM LEAVES THE CHAIR. A shared seat names a chair in ONE room;
  // carried out of the door it would hold the camera at a pose in a floor plan
  // that is no longer loaded, and keep the chair from the next person. Cleared,
  // not stood up — there is nowhere in this room to stand back up to.
  useEffect(() => {
    const s = useWorldStore.getState().seated;
    if (s?.zone && s.zone !== interior) standCleared();
  }, [interior, standCleared]);

  // THE CUE. Armed only once you have actually landed — firing it on 'sitting'
  // would start her walking while the camera is still moving, and the two
  // motions read as one confused shove.
  //
  // The cleanup clearing the timer is what makes standing up early safe: get out
  // of the chair inside the delay and she never sets off at all.
  useEffect(() => {
    if (!cue || phase !== 'seated') return undefined;
    const t = setTimeout(() => stageTo(cue.slug, cue.mark), cue.delayMs ?? 3000);
    return () => clearTimeout(t);
  }, [cue, phase, stageTo]);

  // OUT OF THE CHAIR, BY ANY ROUTE, SENDS HER HOME — the button below, the
  // chair's own Stand up, or world.stand() from the console. Hanging this off
  // the state rather than off the button means there is one way home and no way
  // to leave her stranded at the basin.
  useEffect(() => { if (!seated) clearStaging(); }, [seated, clearStaging]);

  // Leaving the room ends any direction with it. A stage mark names a place in
  // ONE room's floor plan, so carrying it out of the door is meaningless.
  useEffect(() => { clearStaging(); }, [interior, clearStaging]);

  // Shown from the moment you are committed to the chair rather than settled in
  // it: a sit that somehow never completes is precisely the case this exists for.
  //
  // NOT gated on the room having a cue. The point is that being seated always
  // has a visible way out, and "this room has no scripted beat" is the last
  // reason to withhold one — a seat nobody wrote a scene for is exactly where
  // you would be stuck with only the in-scene button, which may be behind you.
  const noteEl = seatNote ? <div style={note} role="status">{seatNote.text}</div> : null;
  if (!seated || phase === 'standing') return noteEl;
  const showClock = !!until && (!keepAlive || until - Date.now() <= QUIET_SHOW_MS);

  return (
    <>
      {noteEl}
      <button
        style={scene ? btnHigh : btnLow}
        onClick={() => { standUp(); clearStaging(); }}
        aria-label={until ? `End this scene and stand up, ${clock(until - Date.now())} left in this seat` : 'End this scene and stand up'}
      >
        Done
        {showClock ? <span style={left}>{clock(until - Date.now())}</span> : null}
      </button>
    </>
  );
}
